import React, { useEffect, useState, useCallback, useMemo } from 'react';
import apiClient from '../../../services/api';
import FearGreedCard from './FearGreedCard';
import IndexStatsCard from './IndexStatsCard';
import VIXStatsCard from './VIXStatsCard';
import BitcoinDrawdownCard from './BitcoinDrawdownCard';
import RangeControl from './RangeControl';
import EconomicIndicatorsSection from './EconomicIndicatorsSection';
import FedWatchSection from './FedWatchSection';
import MacroScorePanel from './MacroScorePanel';

const defaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 365);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
};

function MacroDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(defaultDateRange);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiClient.get('/stock-analyzer/macro/dashboard/');
      setData(resp.data);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const spyHistory = useMemo(
    () => data?.market_indices?.find((i) => i.ticker === 'SPY')?.history || [],
    [data],
  );

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-400">Loading macro data…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-md p-4">
        <p className="text-red-300">{error}</p>
        <button
          onClick={fetchDashboard}
          className="mt-2 px-3 py-1 text-sm rounded-md border border-red-500 text-red-300 hover:bg-red-600/20"
        >
          Retry
        </button>
      </div>
    );
  }

  const fg = data?.fear_greed;
  const indices = data?.market_indices || [];
  const vix = data?.vix_stats;
  const btc = data?.bitcoin;
  const econ = data?.economic_indicators || {};
  const fed = data?.fed_watch || {};
  const errors = data?.errors || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-white">Macro Dashboard</h2>
          <p className="text-sm text-gray-400">
            Market sentiment, index performance, volatility, and crypto drawdown.
          </p>
        </div>
        <button
          onClick={fetchDashboard}
          className="px-3 py-2 text-sm rounded-md border border-blue-500/60 text-blue-300 hover:bg-blue-600/20"
          disabled={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {errors.length > 0 && (
        <div className="bg-yellow-900/20 border border-yellow-700 rounded-md p-3 text-yellow-200 text-sm">
          <strong>Partial data:</strong>{' '}
          {errors.map((e) => `${e.section} — ${e.message}`).join('; ')}
        </div>
      )}

      {/* Composite scores: Economic Health + Market Sentiment, derived from
          every section below. Sits at the top as the headline read. */}
      <MacroScorePanel data={data?.score} spyHistory={spyHistory} />

      {/* Six cards, two per row: VIX + F&G, SPY + DIA, QQQ + BTC.
          The date-range slider below controls VIX, SPY, DIA, QQQ, BTC
          (F&G keeps its own fixed ~1yr history). */}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-6">
          <VIXStatsCard data={vix} dateRange={dateRange} />
        </div>
        <div className="col-span-12 md:col-span-6">
          <FearGreedCard data={fg} />
        </div>
        {indices.map((idx) => (
          <div key={idx.ticker} className="col-span-12 md:col-span-6">
            <IndexStatsCard data={idx} dateRange={dateRange} />
          </div>
        ))}
        <div className="col-span-12 md:col-span-6">
          <BitcoinDrawdownCard data={btc} dateRange={dateRange} />
        </div>
      </div>

      {/* Daily slider for BTC/SPY/DIA/QQQ/VIX, beneath the cards it controls */}
      <RangeControl
        referenceSeries={spyHistory}
        dataKey="close"
        value={dateRange}
        onChange={setDateRange}
        title="Date range"
        caption="Controls BTC, SPY, DIA, QQQ, VIX"
        unitLabel="trading days"
        referenceLabel="SPY reference · drag the handles or click a preset above · BTC shows up to its ~1yr of history"
        yTickFormatter={(v) => `$${Math.round(v)}`}
      />

      {/* Row 5: Fed Watch (FOMC calendar + Polymarket probabilities) */}
      <FedWatchSection data={fed} />

      {/* Row 6: economic indicators section (own slider beneath its cards) */}
      <EconomicIndicatorsSection
        indicators={econ.indicators || []}
        errors={econ.errors || []}
      />
    </div>
  );
}

export default MacroDashboard;
